<!--
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
SPDX-License-Identifier: Apache-2.0
-->

<!-- this is the top-level SFC for the system admin -->

<template>
  <!-- Auth0 callback handler: completes sign-in and redirects to root -->
  <div
    v-if="isCallbackPath"
    class="d-flex justify-content-center align-items-center"
    style="min-height: 50vh"
  >
    <div v-if="callbackError" class="alert alert-danger" role="alert">
      <h5>Authentication Error</h5>
      <p>{{ callbackError }}</p>
      <button class="btn btn-primary" @click="goHome">Back to Home</button>
    </div>
    <div v-else class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3">Completing sign-in...</p>
    </div>
  </div>

  <!-- Loading state while restoring existing Auth0 session -->
  <div
    v-else-if="isLoading"
    class="d-flex justify-content-center align-items-center"
    style="min-height: 50vh"
  >
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Login gate: shown when Auth0 is enabled but the user is not signed in -->
  <div
    v-else-if="!isAuthenticated"
    class="d-flex flex-column justify-content-center align-items-center"
    style="min-height: 60vh"
  >
    <h3 class="mb-3">Virtual Waiting Room System Admin</h3>
    <p class="text-muted mb-4">Sign in to continue.</p>
    <button class="btn btn-primary btn-lg" @click="login">Sign in</button>
  </div>

  <!-- Main UI -->
  <div v-else class="d-flex flex-column m-2">
    <Banner class="mb-3"/>
    <!-- top-right user bar -->
    <div v-if="authEnabled()" class="d-flex justify-content-end mb-2">
      <span class="me-3 text-muted small align-self-center">
        {{ user && (user.email || user.name || user.sub) }}
      </span>
      <button class="btn btn-sm btn-outline-secondary" @click="logout">Sign out</button>
    </div>
    <!-- tenant management view -->
    <div class="w-50 mx-auto">
      <TenantList @tenant-selected="onTenantSelected" />
      <p class="m-2">&nbsp;</p>
      <TenantForm
        :tenant="editingTenant"
        :mode="editingTenant ? 'edit' : 'create'"
        @saved="onTenantSaved"
        @cancelled="editingTenant = null"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import Banner from "./components/Banner.vue";
import TenantList from "./components/TenantList.vue";
import TenantForm from "./components/TenantForm.vue";
import { useTenantStore } from "./stores/tenant";
import { useAuth, handleCallback } from "./composables/useAuth";

const tenantStore = useTenantStore();

const { isAuthenticated, isLoading, user, authEnabled, login, logout } = useAuth();

const editingTenant = ref(null);
const isCallbackPath = ref(window.location.pathname === "/callback");
const callbackError = ref(null);

function goHome() {
  window.history.replaceState({}, "", "/");
  isCallbackPath.value = false;
  callbackError.value = null;
}

onMounted(async () => {
  document.title = "Virtual Waiting Room on AWS System Admin";
  if (isCallbackPath.value) {
    const ok = await handleCallback();
    if (ok) {
      goHome();
    } else {
      callbackError.value = "Failed to complete sign-in. Please try again.";
    }
  }
});

// Auto-fetch tenants once the user is authenticated. The BFF requires a
// valid bearer token, so we wait until isAuthenticated flips to true.
watch(
  isAuthenticated,
  (signedIn) => {
    if (signedIn) {
      tenantStore.fetchTenants();
    }
  },
  { immediate: true }
);

function onTenantSelected(tenant) {
  editingTenant.value = tenant;
}

function onTenantSaved() {
  editingTenant.value = null;
}
</script>

<style>
/* use bootstrap 5.x CSS */
@import "~bootstrap/dist/css/bootstrap.min.css";
</style>
